import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Star, Store, BadgeCheck, Tag, ArrowRight, Building2 } from "lucide-react";
import api from "../api";
import { categoryMeta } from "../mock";
import { FollowButton } from "../components/dashboard/FollowButton";
import { useLang } from "../context/LanguageContext";
import { TrustExplainer } from "../components/TrustExplainer";
import { catLabel } from "../utils/categoryLabel";

const Businesses = () => {
  const { t } = useLang();
  const [businesses, setBusinesses] = useState([]);
  const [cat, setCat] = useState("All");
  const [country, setCountry] = useState("All");
  useEffect(() => { api.get("/businesses").then((r) => setBusinesses(r.data)).catch(() => {}); }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(businesses.map((b) => b.category).filter(Boolean))).sort()], [businesses]);
  const countries = useMemo(() => ["All", ...Array.from(new Set(businesses.map((b) => b.country).filter(Boolean))).sort()], [businesses]);
  const [verifiedOnly, setVerifiedOnly] = useState(() => localStorage.getItem("lp-verified-only") === "1");
  const list = useMemo(() => businesses.filter((b) => (cat === "All" || b.category === cat)
    && (country === "All" || b.country === country) && (!verifiedOnly || b.verified)), [businesses, cat, country, verifiedOnly]);
  const totalDeals = useMemo(() => list.reduce((s, b) => s + (b.deals || 0), 0), [list]);

  const sel = "border border-border rounded-xl px-3 py-2 text-sm bg-card outline-none focus:border-[#16a34a] text-foreground font-medium cursor-pointer";

  return (
    <div className="space-y-7 lp-fade-up">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-[#0f2a1a] text-white p-7 sm:p-9" data-testid="businesses-hero">
        <div className="absolute -left-16 -top-16 h-56 w-56 rounded-full bg-[#16a34a]/40 blur-3xl" />
        <div className="absolute right-10 -bottom-10 h-44 w-44 rounded-full bg-emerald-400/20 blur-2xl" />
        <div className="relative flex flex-wrap items-end justify-between gap-5">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-emerald-200 backdrop-blur">
              <Building2 size={13} /> {t("bl.localGlobalMerchants")}
            </span>
            <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">{t("bl.discoverBusinesses")}</h1>
            <p className="mt-2 max-w-lg text-sm text-emerald-100/80">{t("bl.followYourFavouriteShops")}</p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="rounded-full bg-white/95 px-4 py-1.5 font-bold text-[#0f2a1a]" data-testid="business-count">
              {t("bl.nBusinesses", { n: list.length })}
            </span>
            <span className="rounded-full border border-white/25 px-4 py-1.5 font-semibold text-white/90">{t("bl.nDeals", { n: totalDeals })}</span>
          </div>
        </div>
        <div className="relative mt-5 flex flex-wrap items-center gap-2">
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="business-category-filter" className="rounded-xl bg-white/10 border border-white/20 px-3 py-2 text-sm font-medium text-white outline-none backdrop-blur focus:border-white/60 [&>option]:text-slate-800">
            {categories.map((c) => <option key={c} value={c}>{c === "All" ? t("bl.allCategories") : catLabel(c, t)}</option>)}
          </select>
          <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="business-country-filter" className="rounded-xl bg-white/10 border border-white/20 px-3 py-2 text-sm font-medium text-white outline-none backdrop-blur focus:border-white/60 [&>option]:text-slate-800">
            {countries.map((c) => <option key={c} value={c}>{c === "All" ? t("bl.allCountries") : c}</option>)}
          </select>
          <button
            onClick={() => { const v = !verifiedOnly; setVerifiedOnly(v); localStorage.setItem("lp-verified-only", v ? "1" : "0"); }}
            data-testid="verified-only-toggle"
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${verifiedOnly ? "bg-white text-[#0f2a1a] shadow" : "border border-white/25 text-white/90 hover:bg-white/10"}`}
          >
            <BadgeCheck size={15} /> {t("bl.verifiedOnly")}
          </button>
          <TrustExplainer testid="directory-trust-explainer"
            trigger={<span className="inline-flex items-center gap-1 text-xs font-semibold text-white/80 hover:text-white">{t("trust.whatsThis")}</span>} />
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-muted-foreground bg-card border border-border rounded-2xl p-10 text-center">{t("bl.noBusinessesMatchThese")}</p>
      ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((b) => {
          const meta = categoryMeta(b.category);
          const Icon = meta.icon || Store;
          const banner = b.cover || b.sampleImage;
          return (
            <div key={b.name} className="group relative flex flex-col bg-card border border-border rounded-3xl overflow-hidden shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`business-card-${b.name.replace(/\s+/g, "-").toLowerCase()}`}>
              {/* Banner */}
              <div className="relative h-28 overflow-hidden" style={{ backgroundColor: meta.bg }}>
                {banner ? (
                  <img src={banner} alt={b.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center" style={{ color: meta.color }}><Icon size={40} className="opacity-40" /></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                {b.sampleDiscount != null && (
                  <span className="absolute top-3 left-3 bg-[#16a34a] text-white text-xs font-extrabold px-2.5 py-1 rounded-lg shadow">{b.sampleDiscount}% OFF</span>
                )}
                <span className="absolute top-3 right-3 flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-xs font-bold text-slate-800 shadow" style={{ color: meta.color }}>
                  <Icon size={12} /> {b.category}
                </span>
              </div>

              {/* Logo overlaps banner */}
              <div className="px-5 -mt-8 relative">
                <span className="h-16 w-16 rounded-2xl bg-card border-4 border-card flex items-center justify-center overflow-hidden shadow-md" style={{ backgroundColor: b.logo ? undefined : "#e7f6ec" }}>
                  {b.logo ? <img src={b.logo} alt={b.name} className="h-full w-full object-cover rounded-xl" /> : <Store size={26} className="text-[#16a34a]" />}
                </span>
              </div>

              <div className="flex flex-col flex-1 p-5 pt-3">
                <Link to={`/business/${encodeURIComponent(b.name)}`} className="min-w-0">
                  <h3 className="font-extrabold text-foreground truncate hover:text-[#16a34a] transition-colors flex items-center gap-1.5">
                    {b.name}
                    {b.verified
                      ? <BadgeCheck size={16} className="text-[#16a34a] shrink-0" title={t("vf.verifiedTag")} />
                      : <span data-testid={`independent-tag-${b.name}`} className="text-[10px] font-semibold text-muted-foreground bg-secondary rounded-full px-2 py-0.5 shrink-0">{t("vf.independent")}</span>}
                  </h3>
                </Link>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1 truncate"><MapPin size={14} className="text-[#16a34a] shrink-0" /> {b.location}{b.country ? `, ${b.country}` : ""}</p>

                <div className="flex items-center gap-2 mt-3">
                  <span className="flex items-center gap-1 text-sm font-bold text-foreground bg-secondary px-2.5 py-1 rounded-full"><Star size={14} className="fill-[#f5b942] text-[#f5b942]" /> {b.rating != null ? b.rating : "New"}{b.reviewCount ? ` (${b.reviewCount})` : ""}</span>
                  <span className="flex items-center gap-1 text-sm font-bold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] px-2.5 py-1 rounded-full"><Tag size={13} /> {t("bl.nDeals", { n: b.deals })}</span>
                </div>

                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                  <Link to={`/business/${encodeURIComponent(b.name)}`} data-testid={`view-profile-${b.name.replace(/\s+/g, "-").toLowerCase()}`} className="group/btn flex-1 inline-flex items-center justify-center gap-1.5 bg-secondary hover:bg-[#16a34a] hover:text-white text-foreground font-semibold py-2.5 rounded-xl transition-colors">
                    {t("bl.viewProfile")} <ArrowRight size={15} className="transition-transform duration-300 group-hover/btn:translate-x-0.5" />
                  </Link>
                  <FollowButton business={b.name} className="py-2.5" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default Businesses;
